import type { MelodyLib } from '@tunasong/music-lib'
import type { CaptureDiagnosis } from '@tunasong/music-lib'
import type { Melody, MelodyChordLane, MelodySource, Scale } from '@tunasong/schemas'
import { Button, Checkbox, Dialog, FormControlLabel, useUserConfig } from '@tunasong/ui-lib'
import { useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import { useMelodyRollPlayback } from '../melody-roll/use-melody-roll-playback.js'
import { PitchInputMeter } from '../pitch-training/pitch-input-meter.js'
import { useCaptureMonitor } from '../pitch-training/use-capture-monitor.js'
import { usePlayNotes } from '../player/piano-player.hook.js'
import { CaptureReview } from './capture-review.js'
import { CaptureSettings, type CaptureSettingsValue } from './capture-settings.js'
import {
  AUDIO_LEAD_IN_MS,
  DEFAULT_BARS,
  FREE_TAKE_MAX_MS,
  runMelodyTake,
  TAKE_TAIL_MS,
  type TakePhase,
} from './capture-take.js'
import { buildClickTrack, CLICK_INSTRUMENT, COUNT_IN_CLICKS, countInDurationMs, takeDurationMs } from './click-track.js'
import type { FreeTempoFit } from './free-tempo-fit.js'
import { FreeTempoReview } from './free-tempo-review.js'
import { FreeTempoTap } from './free-tempo-tap.js'
import { hasHeadphoneConsent, withHeadphoneConsent, withoutHeadphoneConsent } from './headphone-consent.js'
import styles from './melody-capture.module.css'
import { framesToRawNotes, useAudioCapture } from './use-audio-capture.js'
import { useCaptureSave } from './use-capture-save.js'
import { useMidiCapture } from './use-midi-capture.js'

/**
 * Capture a melody from a performance: pick how you are playing it, count in, play to the click,
 * review what came back.
 *
 * Two rules this component exists to enforce, both of them about what happens BEFORE a microphone
 * opens:
 *
 *  1. **A take is always started by a click, never by an effect.** A browser will not resume an
 *     `AudioContext` without a user gesture, and an effect on an unstable dependency would re-open
 *     the microphone mid-take. Same rule the Pitch Trainer states for the same reason.
 *  2. **Audio capture does not start until headphones are confirmed.** The click sounds for the
 *     whole take and the tracker segments everything it hears: through a speaker the metronome is
 *     captured on every beat and written into the melody as a note on every beat (§5).
 *
 * The take's tempo, meter and grid become the melody's. They have to: `onset` is in beats and
 * `offsetMs` is measured against a grid line, so notes captured at 96 bpm mean nothing under a
 * melody that claims 120.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

export interface MelodyCaptureProps {
  melody: Melody
  open: boolean
  /**
   * Where a take's recording is filed. The melody entity for a library melody, the host document
   * for an embedded one - a recording belongs somewhere the user can find it again.
   */
  parentId?: string
  onCapture: (melody: Melody) => void
  onClose: () => void
  /**
   * Sing an idea (melody toolkit M2 step 2.5; spec §5.2): the chords to sing over, from the count-in to
   * the take's end. A free take has no click to keep time with, so it plays none.
   */
  backing?: { lane: MelodyChordLane; drone?: Scale }
}

interface ReviewState {
  raw: MelodyLib.RawNote[]
  source: MelodySource
  diagnosis: CaptureDiagnosis | null
  melody: Melody
}

/**
 * A free take, §11.3's retained one.
 *
 * `raw` outlives `fit`: re-tapping clears the fit and never the notes, which is the whole of "a
 * grid chosen several times never costs another performance". Kept beside `ReviewState` rather than
 * folded into it because the clicked path has no tap step and no fit to be missing.
 */
interface FreeTakeState {
  raw: MelodyLib.RawNote[]
  source: MelodySource
  diagnosis: CaptureDiagnosis | null
  melody: Melody
  /** null until the user has tapped a pulse against the take. */
  fit: FreeTempoFit | null
}

const PHASE_TEXT: Record<TakePhase, string> = {
  'lead-in': 'Listening to the room — stay quiet for a moment.',
  'count-in': 'Two knocks, then play.',
  take: 'Recording.',
  quantizing: 'Working out what you played...',
}

/**
 * A free take has no count-in to announce and no bar count to end it, so it says both.
 *
 * `count-in` carries the take's own words because `runMelodyTake` emits that phase either way, back
 * to back with `take` and with nothing awaited between them - so it is on screen for a single tick
 * in every mode. Two knocks are promised there that will never sound; the take's sentence is the
 * true one.
 */
const FREE_TAKE_PHASE = 'Recording. Press Stop when you have finished.'

const FREE_PHASE_TEXT: Record<TakePhase, string> = {
  ...PHASE_TEXT,
  'count-in': FREE_TAKE_PHASE,
  take: FREE_TAKE_PHASE,
}

export const MelodyCapture: FC<MelodyCaptureProps> = props => {
  const { melody, open, parentId, onCapture, onClose, backing } = props

  const [userConfig, updateConfig, hasLoadedConfig] = useUserConfig()
  const confirmedDevices = userConfig?.musicPreferences?.headphoneConfirmedDevices

  const audio = useAudioCapture()
  const midi = useMidiCapture()
  const monitor = useCaptureMonitor()
  const { saveRecording } = useCaptureSave()
  const { playNotes: playClickNotes } = usePlayNotes(CLICK_INSTRUMENT)
  const playback = useMelodyRollPlayback({ melody })

  const [settings, setSettings] = useState<CaptureSettingsValue>(() => ({
    modality: melody.instrumentHint === 'voice' ? 'voice' : 'midi',
    bpm: melody.tempo.bpm,
    meter: melody.tempo.meter,
    grid: melody.grid,
    bars: DEFAULT_BARS,
  }))
  /**
   * The chords under the take: at the take's tempo and meter, with the count-in's beats before beat 1 so
   * the first chord sounds through it. Started on the click's clock (`playWithClick`).
   */
  const backingPlayback = useMelodyRollPlayback({
    melody: { ...melody, notes: [], tempo: { bpm: settings.bpm, meter: settings.meter } },
    ...(backing ? { lane: backing.lane, ...(backing.drone ? { drone: backing.drone } : {}) } : {}),
    isChordsOn: backing !== undefined,
    leadInBeats: COUNT_IN_CLICKS,
  })
  /** The answer given in this session, or null when the user has not answered yet. */
  const [answeredThisSession, setAnsweredThisSession] = useState<boolean | null>(null)
  const [phase, setPhase] = useState<TakePhase | null>(null)
  const [review, setReview] = useState<ReviewState | null>(null)
  const [freeTake, setFreeTake] = useState<FreeTakeState | null>(null)

  /** Every scheduled click, so Stop and unmount can cancel the ones that have not sounded. */
  const timeouts = useRef<number[]>([])
  /** Resolves the take's current wait early - this is the whole of the Stop button. */
  const stopWait = useRef<(() => void) | null>(null)
  /** When the click track started, on `performance.now()`, which is the MIDI events' clock. */
  const clickStartedAt = useRef(0)

  const clearTimeouts = () => {
    for (const handle of timeouts.current) {
      window.clearTimeout(handle)
    }
    timeouts.current = []
  }

  useEffect(
    () => () => {
      clearTimeouts()
      stopWait.current = null
    },
    []
  )

  const isAudioModality = settings.modality !== 'midi'
  const isFreeTempo = settings.modality === 'free'
  const stored = hasHeadphoneConsent(confirmedDevices, audio.deviceLabel)
  /*
   * The answer given in this session OVERRIDES the stored one - it does not merely add to it. The
   * earlier `session || stored` could only ever move the answer upwards, so once a device was
   * remembered the box could not be unchecked and a user who had taken the headphones off had no
   * way to say so.
   *
   * The override still exists, and only for the unnamed device: nothing is stored until the browser
   * has named the microphone, and here nothing names it until a take opens one - so without it, the
   * first capture on a machine could never be confirmed and never start.
   */
  const consented = answeredThisSession ?? stored

  /*
   * A 'yes' given before the browser named the microphone could not be stored on the click: there was no label to
   * store it against. Once a take names the device, save it, or every visit on this machine asks again (GH #2170).
   * Only a 'yes', only once the config has loaded (a write before then fails), and only when it is not stored yet.
   */
  const deviceLabel = audio.deviceLabel
  const musicPreferences = userConfig?.musicPreferences
  useEffect(() => {
    if (!hasLoadedConfig || answeredThisSession !== true || !deviceLabel || stored) {
      return
    }
    updateConfig('musicPreferences', {
      ...musicPreferences,
      headphoneConfirmedDevices: withHeadphoneConsent(musicPreferences?.headphoneConfirmedDevices, deviceLabel),
    })
  }, [hasLoadedConfig, answeredThisSession, deviceLabel, stored, musicPreferences, updateConfig])
  /*
   * §11.2 step 1: a free take has no click, so there is nothing that could bleed out of a speaker
   * and be segmented into notes. The headphone precondition is a rule about the metronome, not a
   * rule about microphones, and applying it here would be cargo-culting §5's reason without §5's
   * cause.
   */
  const canStart = !isAudioModality || isFreeTempo || consented

  const handleConfirmHeadphones = (checked: boolean) => {
    setAnsweredThisSession(checked)
    /*
     * Only a device the browser has named can be remembered, and it has no name until a microphone
     * has been opened once - so the first capture on a machine always asks, which is the right way
     * round for a claim about what the user is physically wearing. Storage is written only when the
     * answer actually changed, in either direction.
     */
    if (checked === stored) {
      return
    }
    const next = checked
      ? withHeadphoneConsent(confirmedDevices, audio.deviceLabel)
      : withoutHeadphoneConsent(confirmedDevices, audio.deviceLabel)
    updateConfig('musicPreferences', { ...userConfig?.musicPreferences, headphoneConfirmedDevices: next })
  }

  const wait = (ms: number) =>
    new Promise<void>(resolve => {
      const handle = window.setTimeout(() => {
        stopWait.current = null
        resolve()
      }, ms)
      timeouts.current.push(handle)
      stopWait.current = () => {
        window.clearTimeout(handle)
        stopWait.current = null
        resolve()
      }
    })

  const handleStop = () => {
    clearTimeouts()
    backingPlayback.stop()
    stopWait.current?.()
  }

  const handleStart = async () => {
    /*
     * A free take has no click, so it has no count-in and no bar count either: it runs until Stop,
     * or until the one-minute backstop. The click track is EMPTY rather than unplayed, so the loop
     * in `playClick` schedules nothing and Stop has nothing to cancel.
     */
    const countInMs = isFreeTempo ? 0 : countInDurationMs(settings.bpm)
    const takeMs = isFreeTempo ? FREE_TAKE_MAX_MS : takeDurationMs(settings)
    const clickTrack = isFreeTempo ? [] : buildClickTrack(settings)
    const captured: Melody = isFreeTempo
      ? /** §11.2 step 4: 4/4 until the user says otherwise. The tempo and grid arrive with the fit. */
        { ...melody, tempo: { ...melody.tempo, meter: { top: 4, bottom: 4 } } }
      : { ...melody, tempo: { bpm: settings.bpm, meter: settings.meter }, grid: settings.grid }

    monitor.reset()
    if (!isAudioModality) {
      midi.arm()
    }

    const result = await runMelodyTake(
      {
        stopPlayback: playback.stop,
        playClick: () => {
          clickStartedAt.current = performance.now()
          if (backing && !isFreeTempo) {
            backingPlayback.playWithClick()
          }
          for (const stab of clickTrack) {
            timeouts.current.push(window.setTimeout(() => playClickNotes(stab.events), stab.delayMs))
          }
        },
        wait,
        onPhase: setPhase,
        startFrames: isAudioModality ? audio.startFrames : undefined,
        startRecording: isAudioModality ? audio.startRecording : undefined,
        onFrame: isAudioModality ? monitor.onFrame : undefined,
      },
      {
        leadInMs: isAudioModality ? AUDIO_LEAD_IN_MS : 0,
        countInMs,
        takeMs,
        tailMs: TAKE_TAIL_MS,
      }
    )
    clearTimeouts()
    backingPlayback.stop()

    if (!isAudioModality) {
      const t0 = clickStartedAt.current + countInMs
      setReview({
        raw: midi.collect(t0, t0 + takeMs + TAKE_TAIL_MS),
        source: { kind: 'midi' },
        diagnosis: null,
        melody: captured,
      })
      setPhase(null)
      return
    }

    const { raw, diagnosis } = framesToRawNotes(result.frames, result.t0Ms, audio.sampleRate)
    /*
     * The recording is saved even when nothing was heard: it is the evidence for the diagnosis, and
     * §2 says the source recording is never discarded.
     */
    const entityId = result.blob ? await saveRecording(result.blob, `${melody.name ?? 'Melody'} take`, parentId) : null
    const source: MelodySource = entityId ? { kind: 'audio', entityId } : { kind: 'audio' }

    /*
     * A free take with no notes has nothing to tap against, so it skips straight to the review -
     * which already owns the three-way capture diagnosis and its numbers. Routing it to the tap
     * panel would ask the user to count along to silence.
     */
    if (isFreeTempo && raw.length > 0) {
      setFreeTake({ raw, source, diagnosis, melody: captured, fit: null })
      setPhase(null)
      return
    }

    setReview({ raw, source, diagnosis, melody: captured })
    setPhase(null)
  }

  const handleAccept = (next: Melody) => {
    onCapture(next)
    setReview(null)
    setFreeTake(null)
    onClose()
  }

  const handleRetake = () => {
    setReview(null)
    setFreeTake(null)
    setPhase(null)
  }

  const handleClose = () => {
    handleStop()
    setReview(null)
    setFreeTake(null)
    setPhase(null)
    onClose()
  }

  /** §11.3: back to the taps, never back to the microphone. The raw notes are not touched. */
  const handleRetap = () => setFreeTake(current => (current ? { ...current, fit: null } : null))

  const handleFit = (fit: FreeTempoFit) => setFreeTake(current => (current ? { ...current, fit } : null))

  return (
    <Dialog open={open} onClose={handleClose} title="Sing an idea" fullWidth={true} maxWidth="md">
      <div className={styles.root}>
        {freeTake?.fit ? (
          <FreeTempoReview
            melody={freeTake.melody}
            raw={freeTake.raw}
            fit={freeTake.fit}
            source={freeTake.source}
            diagnosis={freeTake.diagnosis}
            onAccept={handleAccept}
            onRetap={handleRetap}
            onRetake={handleRetake}
            onCancel={handleClose}
          />
        ) : freeTake ? (
          <FreeTempoTap
            raw={freeTake.raw}
            instrumentHint={freeTake.melody.instrumentHint}
            onFit={handleFit}
            onRetake={handleRetake}
            onCancel={handleClose}
          />
        ) : review ? (
          <CaptureReview
            melody={review.melody}
            raw={review.raw}
            source={review.source}
            diagnosis={review.diagnosis}
            onAccept={handleAccept}
            onRetake={handleRetake}
            onCancel={handleClose}
          />
        ) : phase ? (
          <div className={styles.taking}>
            <span className={styles.phase} role="status">
              {(isFreeTempo ? FREE_PHASE_TEXT : PHASE_TEXT)[phase]}
            </span>
            {isAudioModality ? (
              <PitchInputMeter
                frame={monitor.frame}
                noiseFloorRms={monitor.noiseFloorRms}
                deviceLabel={audio.deviceLabel}
              />
            ) : null}
            <div className={styles.actions}>
              <Button onClick={handleStop}>Stop</Button>
            </div>
          </div>
        ) : (
          <>
            <CaptureSettings value={settings} onChange={setSettings} midiAvailable={midi.hasInput} />

            {isAudioModality && !isFreeTempo ? (
              <div className={styles.headphones}>
                {/* Never disabled: a remembered device is the one case where the user most needs
                    to be able to say the headphones have come off. */}
                <FormControlLabel
                  label="I am wearing headphones"
                  control={<Checkbox checked={consented} onCheckedChange={handleConfirmHeadphones} />}
                />
                <span className={styles.headphonesWhy}>
                  The click plays for the whole take. Out of a speaker it would be recorded too, and every beat of it
                  would end up in the melody as a note.
                </span>
              </div>
            ) : null}

            <div className={styles.actions}>
              <Button disabled={!canStart} onClick={handleStart}>
                Start take
              </Button>
              <Button onClick={handleClose}>Cancel</Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}
